import { z } from 'zod';
import { LARGO_MAXIMO_BUSQUEDA } from './catalogo.js';
import { esquemaConsultaPagina } from './comun.js';
/**
 * Contratos de la demanda no cubierta: lo que nos piden y no tenemos.
 *
 * Rigen las dos reglas de `admin.ts`: todo esquema de escritura es `.strict()`
 * —un campo mal escrito se rechaza con 400 en vez de guardarse a medias— y el
 * esquema NO decide permisos. Que el listado y el cambio de estado sean del
 * equipo, y que el alta sea de un cliente, se comprueba en el módulo.
 *
 * Y una tercera, propia de este flujo: el `clienteId` NO ESTÁ en el esquema de
 * alta. No es un olvido ni algo que el módulo ignore después: si estuviera,
 * existiría la superficie para que un cliente registrara demanda a nombre de
 * otro, y esa es exactamente la clase de escalada que la capa de alcance
 * existe para hacer imposible. Sale del token y de ningún otro lugar.
 */
export const ESTADOS_SOLICITUD = ['abierta', 'catalogada', 'descartada'];
/**
 * Cómo se le explica cada estado a quien lo cambia desde el panel.
 *
 * Vive en el contrato y no en la pantalla porque el rótulo tiene que decir lo
 * mismo que hace el endpoint: `catalogada` no significa "lista", significa "ya
 * está en el catálogo", y una pantalla que lo rotule distinto convierte el
 * estado en un campo que cada quien interpreta a su manera.
 */
export const ETIQUETA_ESTADO_SOLICITUD = {
    abierta: 'Sin resolver',
    catalogada: 'Ya está en el catálogo',
    descartada: 'No la vamos a cubrir',
};
/**
 * Largo del detalle opcional.
 *
 * La búsqueda reutiliza `LARGO_MAXIMO_BUSQUEDA` del catálogo a propósito: es el
 * mismo campo del que sale el texto —la caja de búsqueda que quedó sin
 * resultados—, y dos topes distintos para el mismo dato producirían el caso
 * absurdo de una búsqueda válida que no se puede pedir.
 */
export const LARGO_MAXIMO_DETALLE = 1000;
/**
 * Lo que la persona buscó y no encontró.
 *
 * Se colapsan los espacios y se prohíben los saltos de línea, no por prolijidad:
 * el texto se guarda en la misma columna que el detalle —ver
 * `componerTextoSolicitud` en el dominio— y es la línea en blanco la que separa
 * los dos. Un salto de línea en la búsqueda haría ambigua esa separación.
 *
 * Se exige al menos una letra o un dígito: un texto de puros signos no deja
 * nada al normalizar, así que no se puede agrupar ni entender, y aceptarlo sería
 * llenar la cola de catalogación de filas que nadie puede trabajar.
 */
const esquemaTextoBuscado = z
    .string({ required_error: 'Falta decirnos qué está buscando' })
    .trim()
    // La transformación va ANTES de las validaciones de largo para que el tope se
    // mida sobre lo que de verdad se guarda.
    .transform((v) => v.replace(/\s+/g, ' '))
    .pipe(z
    .string()
    .min(3, 'Escriba al menos tres caracteres para que podamos buscarlo')
    .max(LARGO_MAXIMO_BUSQUEDA, 'El texto es demasiado largo')
    .regex(/[\p{L}\p{N}]/u, 'Escriba con palabras qué necesita'));
export const esquemaCrearSolicitud = z
    .object({
    texto: esquemaTextoBuscado,
    /**
     * Detalle libre: medidas, marca aceptable, para qué equipo es.
     *
     * `nullish` y no `optional` porque el formulario manda el campo vacío como
     * cadena vacía o como null según el navegador, y las tres formas significan
     * lo mismo: no hay detalle.
     *
     * No lleva `.transform` para colapsar el vacío a `null`: un `transform` en
     * un campo de objeto hace que el tipo inferido lo declare obligatorio, y el
     * navegador tendría que mandar `detalle: null` explícito para compilar. El
     * vacío lo resuelve `componerTextoSolicitud`, que trata "sin detalle" y
     * "detalle en blanco" como lo mismo.
     */
    detalle: z
        .string()
        .trim()
        .max(LARGO_MAXIMO_DETALLE, 'El detalle es demasiado largo')
        .nullish(),
    /**
     * Cantidad estimada. Opcional a propósito: exigirla espantaría a quien solo
     * quiere avisar que le falta algo, que es la mayoría de esta cola.
     *
     * El tope es una cota de sanidad, no una regla comercial: un pedido de más
     * de un millón de unidades no se toma por un formulario web.
     */
    cantidad: z
        .number({ invalid_type_error: 'La cantidad debe ser un número' })
        .int('La cantidad debe ser un número entero')
        .min(1, 'La cantidad debe ser al menos 1')
        .max(1_000_000, 'Para esa cantidad conviene que hablemos: escríbanos')
        .nullish(),
    /**
     * Faena de destino, si ya sabe a dónde iría. Se valida que sea del propio
     * cliente en el módulo, donde una faena ajena responde 404 y no 403.
     */
    faenaId: z.string().trim().length(36, 'La faena no es válida').nullish(),
    /**
     * Teléfono para hablarlo, si prefiere que lo llamen.
     *
     * Texto libre y con tope generoso: acá entran anexos, prefijos y horarios
     * escritos a mano. Validar un formato chileno estricto solo lograría que la
     * gente no lo ponga, y un teléfono aproximado sirve infinitamente más que un
     * campo vacío.
     */
    telefono: z.string().trim().max(40, 'El teléfono es demasiado largo').nullish(),
    /**
     * Pidió explícitamente que lo contacten.
     *
     * Es lo que ORDENA la cola del equipo: no es lo mismo «avisen si lo
     * consiguen» que «necesito hablar con alguien». Sin la distinción las dos
     * cosas llegan como la misma fila y se atienden por orden de llegada en vez
     * de por urgencia.
     */
    prefiereLlamada: z.boolean().optional(),
})
    .strict();
export const esquemaConsultaSolicitudes = esquemaConsultaPagina.extend({
    estado: z.enum(ESTADOS_SOLICITUD).optional(),
    clienteId: z.string().trim().length(36, 'El cliente no es válido').optional(),
    /** Busca en el texto tal como se escribió. */
    q: z.string().trim().max(LARGO_MAXIMO_BUSQUEDA).optional(),
});
/**
 * Cambio de estado, con nota opcional.
 *
 * `.strict()` y con al menos un campo: un PATCH vacío responde 400 en vez de
 * 200. Un 200 que no cambió nada deja al operador creyendo que guardó algo.
 */
export const esquemaEditarSolicitud = z
    .object({
    estado: z.enum(ESTADOS_SOLICITUD).optional(),
    /**
     * Nota INTERNA: por qué se catalogó o por qué se descartó. No se le muestra
     * al cliente —el esquema de la base lo dice— y es lo único que permite
     * entender, tres meses después, por qué esta demanda no se cubrió.
     *
     * `null` la borra.
     */
    notaInterna: z
        .string()
        .trim()
        .max(LARGO_MAXIMO_DETALLE, 'La nota es demasiado larga')
        .nullish(),
})
    .strict()
    .refine((v) => v.estado !== undefined || v.notaInterna !== undefined, {
    message: 'No hay nada que cambiar: indique el estado o la nota',
});
/**
 * Cuántas solicitudes se leen como máximo para armar el informe de frecuentes.
 *
 * El agrupamiento se hace en la aplicación y no en SQL, y la razón es la
 * normalización: MySQL puede ignorar acentos con la colación, pero no colapsar
 * espacios ni convertir la coma de "estantería, 2 metros" en un separador. Un
 * `GROUP BY` sobre el texto crudo agruparía solo las coincidencias exactas, es
 * decir casi nada, y el informe se vería construido sin servir para nada.
 *
 * La consecuencia es que hay un tope de filas leídas. El tope NO actúa en
 * silencio: la respuesta trae `truncado`, y la pantalla lo dice. El defecto de
 * la v1 no fue tener un tope, fue que el reporte publicara un porcentaje
 * plausible calculado sobre un subconjunto sin avisar.
 */
export const MUESTRA_FRECUENTES_DEFECTO = 2000;
export const MUESTRA_FRECUENTES_MAXIMA = 20_000;
export const esquemaConsultaFrecuentes = z.object({
    /** Solo los grupos con al menos este número de solicitudes. */
    minimo: z.coerce.number().int().min(1).max(1000).default(1),
    /** Cuántos grupos devolver, ya ordenados por frecuencia. */
    limit: z.coerce.number().int().min(1).max(500).default(50),
    /** Cuántas solicitudes leer para agrupar. Ver el comentario del tope. */
    muestra: z.coerce
        .number()
        .int()
        .min(1)
        .max(MUESTRA_FRECUENTES_MAXIMA)
        .default(MUESTRA_FRECUENTES_DEFECTO),
    /**
     * Por defecto solo lo `abierta`: el informe responde "qué agregamos al
     * catálogo", y lo ya catalogado o descartado no es candidato.
     *
     * `todos` es un valor EXPLÍCITO y no una cadena vacía. Un parámetro vacío que
     * significa "sin filtro" es indistinguible de un parámetro que el formulario
     * mandó mal, y ahí el informe cambiaría de universo sin que nadie lo pidiera.
     */
    estado: z.enum([...ESTADOS_SOLICITUD, 'todos']).default('abierta'),
});
//# sourceMappingURL=solicitudes.js.map